import type { Dayjs } from '~/services/TimeService';

/**
 * Australian fiscal year begins 1 July. Returns the start of the FY that contains `reference`
 * (evaluated in local calendar date; aligns with DEFAULT_TIMEZONE / app dayjs config).
 */
export function getAustralianCurrentFYStart(reference: Dayjs): Dayjs {
  const y = reference.year();
  const july1ThisCalendarYear = reference.clone().year(y).month(6).date(1).startOf('day');
  if (reference.isBefore(july1ThisCalendarYear)) {
    return july1ThisCalendarYear.subtract(1, 'year');
  }
  return july1ThisCalendarYear;
}

/**
 * Earliest date for the parity trend full fetch window: 1 July one year before the current FY start.
 * Keeps the inclusive span from this date through “today” at most ~2 years (FY-aligned, never the
 * ~3y span that “current FY start − 2 years” would produce).
 */
export function getParityTrendFullWindowStart(reference: Dayjs): Dayjs {
  return getAustralianCurrentFYStart(reference).subtract(1, 'year');
}

/**
 * Inclusive list of YYYY-MM-DD strings from `from` through `to` (startOf day).
 * Typical parity-trend window is at most ~730 entries; callers (e.g. ParityTrendSection) usually
 * memoize the result once per mount alongside the slider range.
 */
export function buildCalendarDatesInclusive(from: Dayjs, to: Dayjs): Array<string> {
  const dates: Array<string> = [];
  let cursor = from.clone().startOf('day');
  const end = to.clone().startOf('day');
  if (cursor.isAfter(end)) {
    return [];
  }
  while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
    dates.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
  return dates;
}
